import NoData from '@rio-cloud/rio-uikit/NoData';
import { FormattedMessage } from 'react-intl';

import type { ChargingSessionType } from '@/api/schema';
import { CardsNumberCell } from '@/components/CardsNumberCell';
import Money from '@/components/Money';
import type { Column } from '@/components/Table/Table';
import { DurationCell } from '@/features/chargingActivity/components/DurationCell';
import { useDisplayNames } from '@/hooks/useDisplayNames';
import { formatDateLocalized, formatEnergyValue } from '@/lib/utils';

export const useChargingSessionColumns = (
    t: (arg: string) => string,
    currency: string,
    locale: string
): Column<ChargingSessionType>[] => {
    const formatter = useDisplayNames();
    return [
        {
            key: 'sessionStart',
            label: t('chargingSessions.sessionStart'),
            valueGetter: (originalRow: any) => originalRow.startedAt,
            renderCell: ({ row }) => <span>{formatDateLocalized(new Date(row.startedAt!), locale, true)}</span>,
            sortable: true,
        },
        {
            key: 'sessionEnd',
            label: t('chargingSessions.sessionEnd'),
            valueGetter: (originalRow: any) => originalRow.endedAt,
            renderCell: ({ row }) => <span>{formatDateLocalized(new Date(row.endedAt!), locale, true)}</span>,
            sortable: true,
        },
        {
            key: 'duration',
            label: t('chargingSessions.duration'),
            valueGetter: originalRow => originalRow.duration,
            renderCell: ({ row }) => <DurationCell duration={row.duration ?? ''} />,
            sortable: true,
        },
        {
            key: 'cardNumber',
            label: t('chargingSessions.chargingCardUsed'),
            valueGetter: originalRow => originalRow.token.number,
            renderCell: ({ row }) => <CardsNumberCell id={row.token.number} />,
            sortable: true,
        },
        {
            key: 'cardAlias',
            label: t('chargingSessions.chargingCardAlias'),
            valueGetter: originalRow => originalRow.token.alias,
            renderCell: ({ row }) => (
                <span className={row.token.alias ? 'text-color-black' : 'text-color-gray'}>
                    {row.token.alias ? row.token.alias : t('general.noAliasAdded')}
                </span>
            ),
            sortable: true,
        },
        {
            key: 'energy',
            label: t('general.energyCharged'),
            valueGetter: originalRow => originalRow.energy,
            renderCell: ({ row }) => {
                const { value, unit } = formatEnergyValue(row.energy ?? 0, locale);

                return (
                    <span>
                        {value} {unit}
                    </span>
                );
            },
            sortable: true,
        },
        {
            key: 'totalCost',
            label: t('general.costNet'),
            valueGetter: originalRow => originalRow.totalCost,
            renderCell: ({ row }) => <Money currency={currency} value={row.totalCost ?? 0} />,
            sortable: true,
        },
        {
            key: 'plugType',
            label: t('chargingSessions.plugType'),
            valueGetter: originalRow => originalRow.chargingPoint.plugType,
            renderCell: ({ row }) => <span>{row.chargingPoint.plugType}</span>,
            sortable: true,
        },
        {
            key: 'chargingStationName',
            label: t('chargingSessions.chargingPoint'),
            valueGetter: originalRow => originalRow.chargingPoint.name,
            renderCell: ({ row }) => <span>{row.chargingPoint.name}</span>,
            sortable: true,
        },
        {
            key: 'chargingStationAddress',
            label: t('general.address'),
            valueGetter: originalRow => originalRow.chargingPoint.address,
            renderCell: ({ row }) =>
                row.chargingPoint.address ? (
                    <span>{row.chargingPoint.address}</span>
                ) : (
                    <NoData text={t('general.noData')} className='text-size-14' />
                ),
            sortable: true,
        },
        {
            key: 'chargingStationZipCode',
            label: t('general.zipCode'),
            valueGetter: originalRow => originalRow.chargingPoint.zip,
            renderCell: ({ row }) =>
                row.chargingPoint.zip ? (
                    <span>{row.chargingPoint.zip}</span>
                ) : (
                    <NoData text={t('general.noData')} className='text-size-14' />
                ),
            sortable: true,
        },
        {
            key: 'chargingStationCity',
            label: t('general.city'),
            valueGetter: originalRow => originalRow.chargingPoint.city,
            renderCell: ({ row }) =>
                row.chargingPoint.city ? (
                    <span>{row.chargingPoint.city}</span>
                ) : (
                    <NoData text={t('general.noData')} className='text-size-14' />
                ),
            sortable: true,
        },
        {
            key: 'chargingStationCountry',
            label: t('general.country'),
            valueGetter: originalRow => originalRow.chargingPoint.countryCode,
            renderCell: ({ row }) =>
                formatter && row.chargingPoint.countryCode ? (
                    <span>{formatter(row.chargingPoint.countryCode)}</span>
                ) : (
                    <NoData text={t('general.noData')} className='text-size-14' />
                ),
            sortable: true,
        },
        {
            key: 'invoiceNumber',
            label: t('chargingSessions.invoiceNumber'),
            headerTooltip: (
                <FormattedMessage
                    id='chargingSessions.invoiceNumber.tooltip'
                    values={{ b: chunks => <b>{chunks}</b> }}
                />
            ),
            valueGetter: originalRow => originalRow.invoiceNumber,
            renderCell: ({ row }) => (
                <span className={`${!row.invoiceNumber && 'label label-warning label-condensed'}`}>
                    {row.invoiceNumber ?? t('chargingSessions.pendingInvoice')}
                </span>
            ),
            sortable: true,
        },
        {
            key: 'invoiceDate',
            label: t('chargingSessions.invoiceDate'),
            valueGetter: originalRow => originalRow.invoicedAt,
            renderCell: ({ row }) => (
                <span>{row.invoicedAt ? formatDateLocalized(new Date(row.invoicedAt), locale, false) : ''}</span>
            ),
            sortable: true,
        },
    ];
};
